/**
 * useRouteAccess — backend-authorized route availability decision.
 *
 * Uses the backend route-decision API to determine route availability for
 * every canonical authenticated route. Missing decisions fail closed rather
 * than falling back to static frontend availability.
 */
import { useMemo } from 'react';
import type { ApiSuccessEnvelope, RouteAccessDecision, NavigationAvailability, RouteId } from '@finance/contracts';
// The generated client disambiguates the aliased navigation.route.read
// operations by suffix. Get7 is the actual /api/navigation/route/{routeId}
// endpoint; Get6 is the dashboard summary endpoint and would authorize the
// route against a dashboard id instead of returning a route decision.
import { useNavigationRouteReadGet7 } from '../../api/generated/client';
import { ApiError } from '../../api/http-client';
import { selectApiData } from '../../api/select-api-data';
import { useSession } from '../../features/auth/session-provider';
import { getRouteById } from '../../routes/registry';
import { buildAuthorizationFingerprint, fingerprintToArray } from '../cache/authorization-fingerprint';

function mapAvailabilityToRouteAccessStatus(availability: NavigationAvailability): RouteAccessStatus {
  switch (availability) {
    case 'AVAILABLE':
      return 'AVAILABLE';
    case 'READ_ONLY':
      return 'READ_ONLY';
    case 'LOCKED_PERMISSION':
      return 'RESTRICTED';
    case 'LOCKED_TIER':
      return 'LOCKED';
    case 'FEATURE_DISABLED':
    case 'DATA_UNAVAILABLE':
      return 'UNAVAILABLE';
    case 'PLANNED':
      return 'PLANNED';
    case 'MAINTENANCE':
      return 'MAINTENANCE';
    case 'HIDDEN':
      return 'HIDDEN';
    default:
      return 'HIDDEN';
  }
}

export type RouteAccessStatus =
  | 'LOADING'
  | 'AVAILABLE'
  | 'READ_ONLY'
  | 'PLANNED'
  | 'MAINTENANCE'
  | 'UNAVAILABLE'
  | 'HIDDEN'
  | 'LOCKED'
  | 'RESTRICTED'
  | 'NOT_FOUND'
  | 'SERVICE_ERROR';

export interface RouteAccessResult {
  status: RouteAccessStatus;
  reason?: string;
  remediation?: { action: string; routeId?: string };
  decisionId?: string;
  serviceError?: boolean;
}

export function useRouteAccess(routeId: string): RouteAccessResult {
  const { currentContext, user } = useSession();
  const fingerprint = buildAuthorizationFingerprint(user, currentContext);

  const { data: raw, isLoading, isError, error } = useNavigationRouteReadGet7(routeId, {
    query: {
      enabled: Boolean(routeId),
      queryKey: [
        'navigation',
        'route',
        routeId,
        ...fingerprintToArray(fingerprint),
      ],
      staleTime: 60_000,
      retry: 1,
      select: (response) => selectApiData(response as unknown as ApiSuccessEnvelope<RouteAccessDecision>),
    },
  });

  return useMemo(() => {
    if (isLoading) return { status: 'LOADING' as const };

    if (isError) {
      // The backend conceals unknown and structurally denied routes behind
      // NOT_FOUND; both render as not-found rather than a service error.
      if (error instanceof ApiError && error.status === 404) {
        return {
          status: 'HIDDEN' as const,
          reason: 'ROUTE_NOT_AUTHORIZED',
        };
      }
      return {
        status: 'SERVICE_ERROR' as const,
        reason: 'ROUTE_AUTHORIZATION_ERROR',
        serviceError: true,
      };
    }

    const decision = raw as RouteAccessDecision | undefined;
    if (decision) {
      return {
        status: mapAvailabilityToRouteAccessStatus(decision.availability),
        reason: decision.disabledReasonKey,
        remediation: decision.remediation,
        decisionId: decision.decisionId,
      };
    }

    const route = getRouteById(routeId as RouteId);
    if (!route) {
      return { status: 'NOT_FOUND' as const };
    }

    return {
      status: 'HIDDEN' as const,
      reason: 'ROUTE_NOT_AUTHORIZED',
    };
  }, [raw, isLoading, isError, error, routeId]);
}
